import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
    LuChevronLeft,
    LuChevronRight,
    LuPlay,
    LuPause,
    LuVolume2,
    LuVolumeX,
    LuMoon,
    LuQrCode,
    LuUsers,
    LuSquare,
    LuGamepad2,
    LuSlidersHorizontal,
    LuListVideo,
    LuRepeat2,
    LuX,
} from 'react-icons/lu';

import { useSyncClient } from '~/hooks/models/useClient';
import { useGames } from '~/hooks/models/useGame';
import { Mode, ClientDatabase, IClient } from '~/types/client';
import { trialGoalAction, trialGoalNudge } from '~/types/client/trialGoal';
import { Game } from '~/types/game';
import { GAMES } from '~/config/global.config';
import { GAME_PARAMS_SCHEMAS } from '~/config/gameParamsSchema.config';
import { logEvent } from '~/config/firebase.config';
import { useTick } from '~/hooks/useTick';
import { useLegacyBeat } from '~/hooks/useLegacyBeat';
import { useAnimatorLanguage } from '~/hooks/useAnimatorLanguage';
import { LoadingPage } from '~/pages/LoadingPage';

import { useSendTvLink } from '~/pages/Onboarding/useSendTvLink';
import { useAuth } from '~/services/auth';
import { GameParamsPanel } from './GameParamsPanel';
import { BingoKeypadPanel } from './BingoKeypadPanel';
import { UnquizRegiePanel, type AiQuizStatus } from './UnquizRegiePanel';
import { TrialGoalCard } from './TrialGoalCard';
import { TvHelpCard } from './TvHelpCard';
import { useAiPlaylists } from './useAiPlaylists';
import styles from './AnimatorCockpit.module.css';

/* Le téléphone comme télécommande de la TV : une mini-TV en haut (ce qui passe à
   l'écran), la télécommande tenue en bas, au pouce — quatre tuiles qui s'allument
   quand la fonction est active sur la TV (allumé = actif : QR affiché, pause,
   son, veille), une touche du jeu (réglages ou régie), et le gros bouton OK
   contextuel posé sur trois points d'avancement jeu · joueurs · partie.
   Refonte d'octobre 2026, choisie parmi 8 variantes comparées sur le vrai écran. */

type SheetKind = 'picker' | 'settings' | 'regie';

function Sheet({
    title,
    label,
    open = true,
    onClose,
    children,
}: {
    title?: string;
    label: string;
    /** `false` = masquée mais toujours montée (son contenu garde son état). */
    open?: boolean;
    onClose: () => void;
    children: ReactNode;
}) {
    const { t } = useTranslation();
    const panelRef = useRef<HTMLDivElement>(null);
    // Focus sur la feuille à l'ouverture seulement (pas à chaque rendu : on volerait
    // le focus d'un <select> du panneau de réglages).
    useEffect(() => {
        if (open) panelRef.current?.focus();
    }, [open]);

    return (
        // `display: none` en style et pas l'attribut `hidden` : le `display: flex` du
        // module CSS l'emporterait sur lui.
        <div className={styles.sheetLayer} style={open ? undefined : { display: 'none' }}>
            <button
                type="button"
                className={styles.sheetScrim}
                aria-label={t('animator.cockpit.close')}
                tabIndex={-1}
                onClick={onClose}
            />
            <div
                className={styles.sheet}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                ref={panelRef}
                tabIndex={-1}
            >
                <span className={styles.sheetGrip} aria-hidden="true" />
                <button
                    type="button"
                    className={styles.sheetClose}
                    aria-label={t('animator.cockpit.close')}
                    onClick={onClose}
                >
                    <LuX />
                </button>
                {title && <h2 className={styles.sheetTitle}>{title}</h2>}
                <div className={styles.sheetBody}>{children}</div>
            </div>
        </div>
    );
}

function Confirm({
    title,
    body,
    confirmLabel,
    onCancel,
    onConfirm,
}: {
    title: string;
    body: ReactNode;
    confirmLabel: string;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div className={styles.modalScrim} role="dialog" aria-modal="true" aria-label={title}>
            <div className={styles.modal}>
                <p className={styles.modalTitle}>{title}</p>
                <p className={styles.modalBody}>{body}</p>
                <div className={styles.modalActions}>
                    <button type="button" className={styles.btnGhost} onClick={onCancel}>
                        {t('animator.catalog.cancel')}
                    </button>
                    <button type="button" className={styles.btnDanger} onClick={onConfirm}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Tuile de la télécommande : allumée = la fonction est active sur la TV. */
function Tile({
    icon,
    label,
    on,
    alert = false,
    disabled,
    onClick,
}: {
    icon: ReactNode;
    label: string;
    on: boolean;
    alert?: boolean;
    disabled: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            className={`${styles.tile} ${on ? styles.lit : ''} ${alert ? styles.alert : ''}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={onClick}
        >
            <span className={styles.tileIcon}>{icon}</span>
            <span className={styles.tileLabel}>{label}</span>
        </button>
    );
}

/** `embedded` : la télécommande posée dans la régie double écran (sans en-tête ni pleine hauteur). */
export function CockpitContent({ client, embedded = false }: { client: IClient; embedded?: boolean }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { auth } = useAuth();
    const [sheet, setSheet] = useState<SheetKind | null>(null);
    const [pending, setPending] = useState<Game | null>(null);
    // Toggle optimiste Lancer ↔ Stopper (le cockpit ne suit pas la phase réelle du
    // jeu). Remis à zéro au changement de jeu. `stopConfirm` ouvre la confirmation.
    const [launched, setLaunched] = useState(false);
    const [stopConfirm, setStopConfirm] = useState(false);
    // Réévalue isLive() (TV allumée / éteinte, tuiles désactivées) au fil du temps.
    useTick(30_000);

    // Le cockpit parle la langue de l'ÉTABLISSEMENT, pas celle du téléphone.
    useAnimatorLanguage(client.language);

    // Battement de présence : l'écran masque ses boutons opérateur tant qu'un
    // animateur pilote. Cadence 15 s < fenêtre 30 s.
    const idRef = useRef(client.id);
    idRef.current = client.id;
    useEffect(() => {
        const beat = () => ClientDatabase.setScreenAnimatorPresence(idRef.current, Mode.Anim).catch(() => {});
        beat();
        const t2 = setInterval(beat, 15_000);
        return () => clearInterval(t2);
    }, []);

    // Lien « Créer le quiz » de la carte d'essai : UnQuiz est mis à l'écran, puis
    // la régie (création IA) s'ouvre dès que le changement de jeu revient de
    // Firestore. Horodaté : une intention vieille de plus de 15 s est oubliée.
    const regieIntentRef = useRef(0);

    const cancelPending = () => {
        regieIntentRef.current = 0;
        setPending(null);
    };

    // Échap : ferme la couche du dessus (confirmation, puis feuille).
    useEffect(() => {
        if (!pending && !stopConfirm && !sheet) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            // Échap dans un champ (recherche de playlist, phrase du quiz IA) appartient
            // au champ : ne pas fermer toute la feuille et perdre la saisie.
            const el = e.target as HTMLElement | null;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) return;
            if (pending) cancelPending();
            else if (stopConfirm) setStopConfirm(false);
            else setSheet(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [pending, stopConfirm, sheet]);

    const screen = client.getScreen(Mode.Anim);
    const currentGameID = screen?.gameID;
    // Battement d'une TV ancienne version mémorisé : sinon un téléphone qui écrit
    // après elle la masquerait (TV « hors ligne » jusqu'à son battement suivant).
    const legacyBeatAt = useLegacyBeat(client.id, screen);
    // « Recevoir le lien » : un seul état pour les deux aides (sous la mini-TV et dans
    // « Choisir un jeu »), sinon « Lien envoyé » dans l'une laisse renvoyer depuis l'autre.
    const tvLink = useSendTvLink(auth?.email || '', client.id, 'animator_tv_help');
    const live = screen?.isLive(undefined, legacyBeatAt) ?? false;
    // TV pas ouverte (bars) : `gameID` porte le jeu de la veille (jamais remis à zéro),
    // et le menu de la TV efface à l'ouverture un jeu choisi d'avance (HomeAnimation,
    // anti-valeur périmée). Tout ce qui dit « ce qui est à l'écran » lit donc
    // `screenGameID` (rien quand la TV est fermée) ; choisir un jeu attend la TV.
    const tvClosed = client.mode !== Mode.Auto && !live;
    const screenGameID = tvClosed ? undefined : currentGameID;
    const playerCount = screen?.playerCount ?? 0;
    const qrShown = screen?.showQR ?? false;
    const paused = screen?.paused ?? false;
    const muted = screen?.muted ?? false;
    const idle = screen?.idle ?? false;
    const armed = (screen?.gameParams ?? {}) as { quizId?: string; quizTitle?: string };

    const gameIDs = useMemo(() => (client.gameIDs?.length > 0 ? client.gameIDs : GAMES), [client.gameIDs]);
    const games = useGames(gameIDs);
    const currentGame = games?.find((g) => g.id === screenGameID);
    const schema = currentGameID ? GAME_PARAMS_SCHEMAS[currentGameID] : undefined;
    const isUnquiz = currentGameID === 'unquiz';
    const isBingo = currentGameID === 'bingo';
    // Chargées dès l'ouverture du cockpit, pas de la feuille Réglages (cf GameParamsPanel).
    const aiPlaylists = useAiPlaylists(client.id, currentGameID ?? '');
    // Création de quiz IA en cours / prête, visible sur la touche « Régie » feuille fermée.
    const [aiQuiz, setAiQuiz] = useState<AiQuizStatus>('idle');

    // Le jeu change (ici ou depuis un autre téléphone) : une feuille propre à
    // l'ancien jeu (réglages, régie) se ferme.
    useEffect(() => {
        if (currentGameID === 'unquiz' && Date.now() - regieIntentRef.current < 15_000) {
            regieIntentRef.current = 0;
            setSheet('regie');
            return;
        }
        setSheet((s) => (s === 'picker' ? s : null));
    }, [currentGameID]);

    const applyChange = (gameID: string) => {
        // Intention de la carte d'essai : gardée (et relancée, la confirmation a pu
        // traîner) seulement si c'est bien UnQuiz qui part à l'écran.
        if (regieIntentRef.current) regieIntentRef.current = gameID === 'unquiz' ? Date.now() : 0;
        logEvent('animator_change_game', client.id, { clientID: client.id, gameID });
        ClientDatabase.changeGame(client.id, Mode.Anim, gameID).catch(() => {
            /* l'écran reste sur le jeu courant si l'écriture échoue */
        });
        setLaunched(false); // nouveau jeu = écran lobby → bouton « Lancer »
        setPending(null);
        setSheet(null);
    };

    const toggleQR = () => {
        logEvent('animator_toggle_qr', client.id, { clientID: client.id, show: !qrShown });
        ClientDatabase.setScreenShowQR(client.id, Mode.Anim, !qrShown).catch(() => {
            /* sans effet si l'écriture échoue ; l'écran garde son état courant */
        });
    };

    const launchGame = () => {
        logEvent('animator_launch', client.id, { clientID: client.id, gameID: currentGameID });
        ClientDatabase.requestLaunch(client.id, Mode.Anim).catch(() => {
            /* sans effet si l'écriture échoue ; l'animateur peut réessayer */
        });
        setLaunched(true);
    };

    const stopGame = () => {
        logEvent('animator_stop', client.id, { clientID: client.id, gameID: currentGameID });
        ClientDatabase.requestStop(client.id, Mode.Anim).catch(() => {
            /* sans effet si l'écriture échoue ; l'animateur peut réessayer */
        });
        setLaunched(false);
        setStopConfirm(false);
    };

    const toggleControl = (patch: { paused?: boolean; muted?: boolean; idle?: boolean }) => {
        const [field, value] = Object.entries(patch)[0];
        logEvent('animator_control', client.id, { clientID: client.id, field, value });
        ClientDatabase.setScreenControls(client.id, Mode.Anim, patch).catch((e) =>
            console.error('[animator] setScreenControls failed', e)
        );
    };

    const onPick = (game: Game) => {
        if (tvClosed) {
            setSheet('picker');
            return;
        }
        // Confirmation seulement si un jeu est déjà à l'écran (évite de couper une
        // partie par une fausse manip au pouce).
        if (currentGameID && currentGameID !== game.id) {
            setPending(game);
        } else {
            applyChange(game.id);
        }
    };

    // Objectif « 3 soirées » de l'essai : groupe traité seulement (champ absent
    // ailleurs), jamais après la fin de l'essai ni une fois l'objectif atteint,
    // ni chez un lieu résilié ou en pause.
    const goal = client.disabled || client.isPaused ? null : trialGoalNudge(client.trialGoal);
    const goalGame = goal ? games?.find((g) => g.id === goal.gameID) : undefined;
    const goalAction = goal ? trialGoalAction(goal.next, screenGameID, !!goalGame) : null;
    // Catalogue chargé et jeu proposé disponible (ou déjà à l'écran) : sinon l'idée
    // serait une impasse pour ce lieu, on n'affiche rien.
    const showGoal = !!goal && !!games && (!!goalGame || screenGameID === goal.gameID);
    const onGoalAction = () => {
        if (!goal) return;
        logEvent('animator_trial_goal_click', client.id, {
            clientID: client.id,
            next: goal.next,
            night: goal.night,
            action: goalAction,
        });
        if (goalAction === 'openRegie') {
            setSheet('regie');
        } else if (goalAction === 'pick' && goalGame) {
            if (goal.next === 'cityquiz') regieIntentRef.current = Date.now();
            onPick(goalGame);
        }
    };

    const gameTitle = currentGame?.name || (screenGameID ?? t('animator.cockpit.homeMenu'));
    const cover = currentGame?.cover || currentGame?.icon;

    // QR / Pause / Son / Veille n'agissent que sur l'écran EN JEU : sur le menu, la TV
    // n'écoute que le choix du jeu (AnimatorScreenControls et le QR géant ne sont
    // montés que dans ScreenApp). Des tuiles actives y seraient des boutons morts :
    // désactivées, et éteintes (un « Son » allumé mais grisé laisserait croire l'inverse).
    const controlsActive = live && !!currentGameID;

    // Bouton OK : Choisir (menu) → Lancer (jeu prêt) → Stopper (partie lancée).
    let okTone = styles.okPick;
    let okIcon = <LuGamepad2 />;
    let okLabel = t('animator.cockpit.pickGame');
    let onOk = () => setSheet('picker');
    if (screenGameID && !launched) {
        okTone = styles.okGo;
        okIcon = <LuPlay />;
        okLabel = t('animator.cockpit.launchShort');
        onOk = launchGame;
    } else if (screenGameID) {
        okTone = styles.okStop;
        okIcon = <LuSquare />;
        okLabel = t('animator.cockpit.stopShort');
        onOk = () => setStopConfirm(true);
    }

    // Trois points d'avancement : jeu à la TV → des joueurs connectés → partie lancée.
    const steps = [
        { id: 'game', done: !!screenGameID, label: t('animator.cockpit.steps.game') },
        { id: 'players', done: playerCount > 0, label: t('animator.cockpit.steps.players') },
        { id: 'play', done: !!screenGameID && launched, label: t('animator.cockpit.steps.play') },
    ];
    const nowStep = steps.findIndex((s) => !s.done);

    // État de la mini-TV : éteinte > veille > menu > jeu.
    let tvState = styles.tvGame;
    if (!live) tvState = styles.tvOff;
    else if (idle) tvState = styles.tvIdle;
    else if (!currentGameID) tvState = styles.tvMenu;

    // Touche du jeu : réglages (jeux à schéma) ou régie (UnQuiz) — même place,
    // même forme, juste au-dessus du bouton OK (on règle, puis on lance).
    let gameKey: ReactNode = null;
    if (schema) {
        gameKey = (
            <button type="button" className={styles.gameKey} onClick={() => setSheet('settings')}>
                <span className={styles.gameKeyIcon}>
                    <LuSlidersHorizontal />
                </span>
                <span className={styles.gameKeyText}>
                    <span className={styles.gameKeyTitle}>{t(schema.titleKey)}</span>
                </span>
                <LuChevronRight className={styles.gameKeyChevron} aria-hidden="true" />
            </button>
        );
    } else if (isUnquiz) {
        gameKey = (
            <button type="button" className={styles.gameKey} onClick={() => setSheet('regie')}>
                <span className={styles.gameKeyIcon}>
                    <LuListVideo />
                </span>
                <span className={styles.gameKeyText}>
                    <span className={styles.gameKeyTitle}>{t('animator.cockpit.regie')}</span>
                    <span className={styles.gameKeySub}>
                        {aiQuiz === 'idle'
                            ? armed.quizTitle || armed.quizId || t('animator.unquiz.noneArmed')
                            : t(`animator.cockpit.regieAi.${aiQuiz}`)}
                    </span>
                </span>
                <LuChevronRight className={styles.gameKeyChevron} aria-hidden="true" />
            </button>
        );
    }

    return (
        <div className={`${styles.page} ${embedded ? styles.embedded : ''}`}>
            <div className={styles.shell}>
                <header className={`${styles.header} ${styles.reveal}`}>
                    <button
                        type="button"
                        className={styles.back}
                        aria-label={t('animator.cockpit.backToVenues')}
                        onClick={() => navigate('/animator')}
                    >
                        <LuChevronLeft />
                    </button>
                    <h1 className={styles.venue}>{client.name}</h1>
                </header>

                {/* Mini-TV : reflète l'écran. Toucher = changer de jeu (TV fermée : liste grisée + consigne). */}
                <section className={`${styles.tvCol} ${styles.reveal} ${styles.d1}`}>
                    <button
                        type="button"
                        className={styles.tvButton}
                        aria-label={[
                            t('animator.cockpit.tv'),
                            tvClosed ? t('animator.cockpit.offlineHint') : gameTitle,
                            live ? t('animator.cockpit.live') : t('animator.cockpit.offline'),
                            live && t('animator.cockpit.players', { count: playerCount }),
                            !tvClosed && t('animator.cockpit.changeGame'),
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                        onClick={() => setSheet('picker')}
                    >
                        <span className={styles.tvBezel}>
                            <span className={`${styles.tvScreen} ${tvState}`}>
                                {screenGameID && cover && <img className={styles.tvCover} src={cover} alt="" />}
                                {screenGameID && !cover && (
                                    <span className={styles.tvFallback}>
                                        <LuGamepad2 />
                                    </span>
                                )}
                                {!screenGameID && (
                                    <span className={styles.tvMenuGrid} aria-hidden="true">
                                        {(games ?? []).slice(0, 8).map((g) => (
                                            <img key={g.id} src={g.icon} alt="" />
                                        ))}
                                    </span>
                                )}

                                {/* États qui recouvrent l'image, comme sur la TV */}
                                {live && paused && !idle && (
                                    <span className={styles.tvCenterGlyph} aria-hidden="true">
                                        <LuPause />
                                    </span>
                                )}
                                {live && idle && (
                                    <span className={styles.tvCenterGlyph} aria-hidden="true">
                                        <LuMoon />
                                    </span>
                                )}
                                {!live && (
                                    <span className={styles.tvOffText}>
                                        <span className={styles.tvOffHint}>{t('animator.cockpit.offlineHint')}</span>
                                    </span>
                                )}

                                {live && (
                                    <span className={styles.tvLive}>
                                        <span className={styles.tvLiveDot} />
                                        {t('animator.cockpit.live')}
                                    </span>
                                )}
                                {live && (
                                    <span className={styles.tvBadges}>
                                        {muted && (
                                            <span className={`${styles.tvBadge} ${styles.tvBadgeMuted}`}>
                                                <LuVolumeX />
                                            </span>
                                        )}
                                        {qrShown && (
                                            <span className={`${styles.tvBadge} ${styles.tvBadgeQr}`}>
                                                <LuQrCode />
                                            </span>
                                        )}
                                        <span className={styles.tvBadge}>
                                            <LuUsers />
                                            <span className={styles.tvCount}>{playerCount}</span>
                                        </span>
                                    </span>
                                )}
                            </span>
                        </span>
                        <span className={styles.tvStand} aria-hidden="true" />
                    </button>

                    {!tvClosed && (
                        <div className={styles.caption}>
                            <button
                                type="button"
                                className={styles.captionGame}
                                aria-label={`${gameTitle} · ${t('animator.cockpit.changeGame')}`}
                                onClick={() => setSheet('picker')}
                            >
                                <span className={styles.captionName}>{gameTitle}</span>
                            </button>
                            {currentGameID && (
                                <button type="button" className={styles.changeChip} onClick={() => setSheet('picker')}>
                                    <LuRepeat2 aria-hidden="true" />
                                    <span>{t('animator.cockpit.changeGame')}</span>
                                </button>
                            )}
                        </div>
                    )}
                </section>

                {/* Pas d'écran en face : où ouvrir Unboared + « Recevoir le lien ».
                    Disparaît seule dès que l'écran bat. */}
                {tvClosed && (
                    <TvHelpCard className={`${styles.reveal} ${styles.d2}`} clientID={client.id} link={tvLink} />
                )}

                {/* Essai : « Soirée X sur 3 » + idée du soir. Téléphone seulement, jamais la TV. */}
                {showGoal && goal && (
                    <TrialGoalCard
                        className={`${styles.goal} ${styles.reveal} ${styles.d2}`}
                        night={goal.night}
                        target={goal.target}
                        next={goal.next}
                        onAction={goalAction ? onGoalAction : undefined}
                    />
                )}

                {/* Télécommande, au pouce : tuiles → touche du jeu → OK → avancement */}
                <section className={`${styles.remote} ${styles.reveal} ${styles.d2}`}>
                    <div className={styles.tiles}>
                        <Tile
                            icon={<LuQrCode />}
                            label={t('animator.cockpit.qrShort')}
                            /* Le QR marche aussi sur le menu de la TV (QR des joueurs entre deux jeux). */
                            on={live && qrShown}
                            disabled={!live}
                            onClick={toggleQR}
                        />
                        <Tile
                            icon={<LuPause />}
                            label={t('animator.cockpit.pause')}
                            on={controlsActive && paused}
                            disabled={!controlsActive}
                            onClick={() => toggleControl({ paused: !paused })}
                        />
                        {/* Allumée = le son joue sur la TV ; muet = éteinte + alerte rose. */}
                        <Tile
                            icon={muted ? <LuVolumeX /> : <LuVolume2 />}
                            label={muted ? t('animator.cockpit.muted') : t('animator.cockpit.sound')}
                            on={controlsActive && !muted}
                            alert={controlsActive && muted}
                            disabled={!controlsActive}
                            onClick={() => toggleControl({ muted: !muted })}
                        />
                        <Tile
                            icon={<LuMoon />}
                            label={t('animator.cockpit.sleep')}
                            on={controlsActive && idle}
                            disabled={!controlsActive}
                            onClick={() => toggleControl({ idle: !idle })}
                        />
                    </div>

                    {gameKey}

                    <div className={styles.okZone}>
                        <div className={styles.okRing}>
                            <button type="button" className={`${styles.ok} ${okTone}`} onClick={onOk} disabled={!live}>
                                <span className={styles.okIcon}>{okIcon}</span>
                                <span className={styles.okLabel}>{okLabel}</span>
                            </button>
                        </div>

                        <ol
                            className={`${styles.steps} ${live ? '' : styles.stepsOff}`}
                            aria-label={t('animator.cockpit.steps.label')}
                        >
                            {steps.map((s, i) => (
                                <li
                                    key={s.id}
                                    className={`${styles.step} ${s.done ? styles.stepDone : ''} ${i === nowStep ? styles.stepNow : ''}`}
                                >
                                    <span className={styles.stepDot} aria-hidden="true" />
                                    <span className={styles.stepLabel}>{s.label}</span>
                                    <span className={styles.srOnly}>
                                        {s.done ? t('animator.cockpit.steps.done') : t('animator.cockpit.steps.todo')}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* Disco Bingo — le pavé EST le jeu (un numéro toutes les ~20 s) :
                    il reste en ligne, sans tap de plus. */}
                {isBingo && (
                    <section className={`${styles.deck} ${styles.reveal} ${styles.d2}`}>
                        <BingoKeypadPanel key="bingo-keypad" client={client} />
                    </section>
                )}
            </div>

            {sheet === 'picker' && (
                <Sheet
                    title={t('animator.catalog.title')}
                    label={t('animator.catalog.title')}
                    onClose={() => setSheet(null)}
                >
                    {/* TV pas ouverte : ouvrir la TV d'abord, puis choisir (jeux grisés). */}
                    {tvClosed && <TvHelpCard clientID={client.id} where="picker" link={tvLink} />}
                    {!games ? (
                        <p className={styles.muted}>{t('animator.catalog.loading')}</p>
                    ) : (
                        <div className={`${styles.grid} ${tvClosed ? styles.gridOff : ''}`}>
                            {games.map((game) => {
                                const isCurrent = game.id === screenGameID;
                                return (
                                    <button
                                        key={game.id}
                                        type="button"
                                        className={`${styles.pick} ${isCurrent ? styles.pickCurrent : ''}`}
                                        disabled={isCurrent || tvClosed}
                                        aria-current={isCurrent || undefined}
                                        onClick={() => onPick(game)}
                                    >
                                        <span className={styles.pickArt}>
                                            <img src={game.cover || game.icon} alt="" />
                                            {isCurrent && (
                                                <span className={styles.pickTag}>{t('animator.catalog.current')}</span>
                                            )}
                                        </span>
                                        <span className={styles.pickName}>{game.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </Sheet>
            )}

            {sheet === 'settings' && currentGameID && schema && (
                <Sheet label={t(schema.titleKey)} onClose={() => setSheet(null)}>
                    {/* Le panneau porte son propre titre (pas de .panelHost). key={gameID} :
                        remonte le panneau au changement de jeu. */}
                    <GameParamsPanel
                        key={currentGameID}
                        client={client}
                        screen={screen}
                        live={live}
                        gameID={currentGameID}
                        aiPlaylists={aiPlaylists}
                    />
                </Sheet>
            )}

            {/* Régie UnQuiz : montée tant qu'UnQuiz est à l'écran, la feuille ne fait que
                se masquer. Démonter le panneau couperait une création de quiz par IA en
                cours (~30 s, annulée au démontage par useQuizGeneration) et perdrait la
                phrase tapée — fermer la feuille d'un geste du pouce ne doit rien casser. */}
            {isUnquiz && (
                <Sheet label={t('animator.cockpit.regie')} open={sheet === 'regie'} onClose={() => setSheet(null)}>
                    <div className={styles.panelHost}>
                        <UnquizRegiePanel
                            key="unquiz-regie"
                            client={client}
                            screen={screen}
                            live={live}
                            onAiStatus={setAiQuiz}
                        />
                    </div>
                </Sheet>
            )}

            {pending && (
                <Confirm
                    title={t('animator.catalog.switchTitle')}
                    body={
                        <Trans
                            i18nKey="animator.catalog.switchBody"
                            values={{ game: pending.name }}
                            components={[<strong key="game" />]}
                        />
                    }
                    confirmLabel={t('animator.catalog.switchConfirm', { game: pending.name })}
                    onCancel={cancelPending}
                    onConfirm={() => applyChange(pending.id)}
                />
            )}

            {stopConfirm && (
                <Confirm
                    title={t('animator.catalog.stopTitle')}
                    body={t('animator.catalog.stopBody')}
                    confirmLabel={t('animator.catalog.stopConfirm')}
                    onCancel={() => setStopConfirm(false)}
                    onConfirm={stopGame}
                />
            )}
        </div>
    );
}

/**
 * Animator Control — poste de pilotage d'un établissement, en télécommande.
 * Changement de jeu, lancer/stopper, QR, pause/son/veille : même tube Firestore
 * que l'original (ClientDatabase → screenInfos.anim).
 */
export function AnimatorCockpit() {
    const { t } = useTranslation();
    const { clientID } = useParams();
    const client = useSyncClient(clientID || '');

    if (!client) {
        return <LoadingPage>{t('animator.cockpit.connecting')}</LoadingPage>;
    }
    return <CockpitContent client={client} />;
}
